import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
    LayoutDashboard, Users, Package as PackageIcon, Receipt, LineChart, Settings, Trophy, ShieldCheck, UsersRound,
    Plus, ArrowUpRight, ArrowDownRight, ChevronRight, CalendarClock, BadgeCheck, Search, Edit3, Trash2, Pause, Play, Sparkles, UserCheck, Download, Copy, Check, ExternalLink, QrCode, X, Send, Eye, EyeOff, Inbox, Mail, Phone, MessageSquare, ToggleLeft, ToggleRight,
} from "lucide-react";
import { api, formatGBP, formatNumber, formatDate, formatApiErrorDetail } from "../../lib/api";
import { useAuth, applyTenantTheme } from "../../lib/auth";
import {
    TopBar, KpiCard, Pill, FooterStrip, SidebarShell, Modal, Field, TextInput, Select, PrimaryButton, GhostButton, Crest, TenantThemer,
} from "../../components/shell";
import FansTab from "./FansTab";
import PitchTab from "./PitchTab";
import PlayersTab from "./PlayersTab";
import MembersTab from "./MembersTab";
import DigitalAdsForm from "../../components/DigitalAdsForm";

// Trigger a browser download of the branded sponsor-pack PDF for the current tenant.
// Uses an authenticated fetch (cookie-based) so the file streams directly to the user.
const downloadSponsorPack = async (tenantId, queryString = "") => {
    try {
        const res = await api.get(`/sponsor-pack${queryString}`, { responseType: "blob" });
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${tenantId || "club"}-sponsor-pack.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    } catch (e) {
        // eslint-disable-next-line no-alert
        alert("Couldn't download the sponsor pack — please try again.");
    }
};

const navItems = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "sponsors", label: "Sponsors", icon: Users },
    { key: "packages", label: "Packages", icon: PackageIcon },
    { key: "fans", label: "Fan revenue", icon: UserCheck },
    { key: "players", label: "Players", icon: ShieldCheck },
    { key: "enquiries", label: "Enquiries", icon: Inbox },
    { key: "members", label: "Team", icon: UsersRound },
    { key: "invoices", label: "Invoices", icon: Receipt, soon: true },
    { key: "impressions", label: "Impressions", icon: LineChart, soon: true },
    { key: "pitch", label: "Virtual pitch", icon: Trophy },
    { key: "settings", label: "Settings", icon: Settings },
];

const STATUS_TONE = (s) => ({
    "Active": "active",
    "Pending payment": "pending",
    "Paused": "paused",
    "Cancelled": "cancelled",
}[s] || "neutral");

const TIER_OPTIONS = ["Headline", "Stand", "Perimeter", "Matchball", "Programme"];
const STATUS_OPTIONS = ["Active", "Pending payment", "Paused", "Cancelled"];

export default function ClubDashboard() {
    const { user } = useAuth();
    const [params] = useSearchParams();
    const overrideTenantId = params.get("as_tenant");
    const isPlatformPreview = user?.role === "platform_owner" && overrideTenantId;
    const tenantId = overrideTenantId || user?.tenant_id;

    const [tab, setTab] = useState("overview");
    const [tenant, setTenant] = useState(user?.tenant || null);
    const [kpis, setKpis] = useState(null);
    const [sponsors, setSponsors] = useState([]);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);

    const [sponsorModal, setSponsorModal] = useState(null); // {mode:'create'|'edit', sponsor}
    const [inviteModal, setInviteModal] = useState(null); // sponsor being invited
    const [packageModal, setPackageModal] = useState(null);

    const queryString = isPlatformPreview ? `?tenant_id=${tenantId}` : "";

    const load = async () => {
        setLoading(true);
        try {
            const [tRes, kRes, sRes, pRes] = await Promise.all([
                isPlatformPreview ? api.get(`/tenants/${tenantId}`) : api.get("/auth/me").then((r) => ({ data: r.data.tenant })),
                api.get(`/dashboard/kpis${queryString}`),
                api.get(`/sponsors${queryString}`),
                api.get(`/packages${queryString}`),
            ]);
            setTenant(tRes.data);
            setKpis(kRes.data);
            setSponsors(sRes.data);
            setPackages(pRes.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

    useEffect(() => {
        applyTenantTheme(tenant);
        return () => applyTenantTheme(null);
    }, [tenant]);

    const onCreateSponsor = async (draft) => {
        await api.post(`/sponsors${queryString}`, draft);
        setSponsorModal(null);
        await load();
    };
    const onUpdateSponsor = async (id, patch) => {
        await api.patch(`/sponsors/${id}`, patch);
        setSponsorModal(null);
        await load();
    };
    const onDeleteSponsor = async (s) => {
        if (!window.confirm(`Remove sponsor ${s.name}?`)) return;
        await api.delete(`/sponsors/${s.id}`);
        await load();
    };
    const onTogglePause = async (s) => {
        const next = s.status === "Paused" ? "Active" : "Paused";
        await api.patch(`/sponsors/${s.id}`, { status: next });
        await load();
    };

    const onTenantUpdate = async (patch) => {
        const { data } = await api.patch(`/tenants/${tenant.id}`, patch);
        setTenant(data);
    };

    const onCreatePackage = async (draft) => {
        await api.post(`/packages${queryString}`, draft);
        setPackageModal(null);
        await load();
    };
    const onUpdatePackage = async (id, patch) => {
        await api.patch(`/packages/${id}`, patch);
        setPackageModal(null);
        await load();
    };
    const onDeletePackage = async (p) => {
        if (!window.confirm(`Delete package ${p.name}?`)) return;
        try {
            await api.delete(`/packages/${p.id}`);
            await load();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    if (!tenant && !loading) {
        return <div className="min-h-screen bg-[var(--sb-page)] text-white flex items-center justify-center text-sm text-zinc-400">No tenant assigned.</div>;
    }

    return (
        <div data-testid="club-dashboard" className="min-h-screen text-white bg-[var(--sb-page)]">
            <TenantThemer tenant={tenant} />
            <div className="grid grid-cols-12 min-h-screen">
                <SidebarShell
                    testId="club-sidebar"
                    tenantTitle={
                        <>
                            <Crest tenant={tenant} size={40} />
                            <div>
                                <div className="font-display font-bold text-sm leading-tight">{tenant?.short_name || tenant?.name || "Club"}</div>
                                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{tenant?.season || "2025/26"}</div>
                            </div>
                        </>
                    }
                    tenantSubtitle={tenant?.subdomain}
                    navItems={navItems}
                    activeKey={tab}
                    onNav={setTab}
                    footer={
                        <div className="p-3 border-t hairline">
                            {isPlatformPreview && (
                                <div className="px-3 py-2 mb-2 border hairline text-[10px] uppercase tracking-ultra-wide text-zinc-400" data-testid="platform-preview-banner">
                                    Platform preview · viewing as tenant
                                </div>
                            )}
                            <div className="flex items-center gap-2.5 px-3 py-2.5">
                                <div className="w-7 h-7 bg-white/10 flex items-center justify-center text-xs font-bold">{(user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm truncate">{user?.name}</div>
                                    <div className="text-[10px] text-zinc-500 truncate">{user?.title || user?.role?.replace("_", " ")}</div>
                                </div>
                            </div>
                        </div>
                    }
                />

                <main className="col-span-12 lg:col-span-10">
                    <TopBar
                        breadcrumb={
                            <>
                                <span>{tenant?.name}</span>
                                <ChevronRight size={12} className="text-zinc-700 mx-2" />
                                <span className="text-white capitalize">{tab}</span>
                            </>
                        }
                        right={
                            tab === "sponsors" ? (
                                <PrimaryButton tenantTheme onClick={() => setSponsorModal({ mode: "create", sponsor: null })} data-testid="add-sponsor-btn">
                                    <Plus size={14} strokeWidth={2.4} /> Add sponsor
                                </PrimaryButton>
                            ) : tab === "packages" ? (
                                <div className="flex items-center gap-2">
                                    <GhostButton onClick={() => downloadSponsorPack(tenantId, queryString)} data-testid="download-sponsor-pack-btn">
                                        <Download size={13} /> Sponsor pack
                                    </GhostButton>
                                    <PrimaryButton tenantTheme onClick={() => setPackageModal({ mode: "create", package: null })} data-testid="add-package-btn">
                                        <Plus size={14} strokeWidth={2.4} /> Add package
                                    </PrimaryButton>
                                </div>
                            ) : (
                                <PrimaryButton tenantTheme onClick={() => setSponsorModal({ mode: "create", sponsor: null })} data-testid="overview-add-sponsor">
                                    <Plus size={14} strokeWidth={2.4} /> Add sponsor
                                </PrimaryButton>
                            )
                        }
                    />

                    <div className="px-6 md:px-10 py-10 space-y-10">
                        {tab === "overview" && <OverviewTab tenant={tenant} kpis={kpis} sponsors={sponsors} packages={packages} loading={loading} />}
                        {tab === "sponsors" && (
                            <SponsorsTab
                                sponsors={sponsors}
                                packages={packages}
                                tenant={tenant}
                                onEdit={(s) => setSponsorModal({ mode: "edit", sponsor: s })}
                                onDelete={onDeleteSponsor}
                                onTogglePause={onTogglePause}
                                onInvite={(s) => setInviteModal(s)}
                            />
                        )}
                        {tab === "packages" && (
                            <PackagesTab
                                packages={packages}
                                onEdit={(p) => setPackageModal({ mode: "edit", package: p })}
                                onDelete={onDeletePackage}
                            />
                        )}
                        {tab === "fans" && <FansTab tenantId={tenantId} queryString={queryString} />}
                        {tab === "players" && <PlayersTab tenant={tenant} queryString={queryString} sponsors={sponsors} />}
                        {tab === "members" && <MembersTab tenant={tenant} />}
                        {tab === "pitch" && <PitchTab tenantId={tenantId} queryString={queryString} />}
                        {(tab === "invoices" || tab === "impressions") && <ComingSoon tab={tab} />}
                        {tab === "enquiries" && <EnquiriesTab tenant={tenant} />}
                        {tab === "settings" && <SettingsTab tenant={tenant} onTenantUpdate={onTenantUpdate} />}
                        <FooterStrip tenant={tenant} />
                    </div>
                </main>
            </div>

            {sponsorModal && (
                <SponsorFormModal
                    initial={sponsorModal.sponsor}
                    packages={packages}
                    onClose={() => setSponsorModal(null)}
                    onSubmit={(draft) => sponsorModal.mode === "create" ? onCreateSponsor(draft) : onUpdateSponsor(sponsorModal.sponsor.id, draft)}
                />
            )}
            {packageModal && (
                <PackageFormModal
                    initial={packageModal.package}
                    onClose={() => setPackageModal(null)}
                    onSubmit={(draft) => packageModal.mode === "create" ? onCreatePackage(draft) : onUpdatePackage(packageModal.package.id, draft)}
                />
            )}
            {inviteModal && (
                <SponsorInviteModal
                    sponsor={inviteModal}
                    tenant={tenant}
                    onClose={() => setInviteModal(null)}
                />
            )}
        </div>
    );
}

// --- OVERVIEW ---
const CapacityDonut = ({ value }) => {
    const r = 56;
    const c = 2 * Math.PI * r;
    const offset = c - (value / 100) * c;
    return (
        <svg width="140" height="140" viewBox="0 0 140 140" aria-hidden="true">
            <circle cx="70" cy="70" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
            <circle cx="70" cy="70" r={r} stroke="var(--tenant-primary)" strokeWidth="6" fill="none" strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="butt" transform="rotate(-90 70 70)" />
            <text x="70" y="68" textAnchor="middle" fill="#fff" fontSize="28" fontWeight="800" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>{value}%</text>
            <text x="70" y="86" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10" letterSpacing="1.6">SOLD</text>
        </svg>
    );
};

const OverviewTab = ({ tenant, kpis, sponsors, packages, loading }) => {
    const { user } = useAuth();
    if (loading || !kpis) return <div className="border hairline p-12 text-center text-zinc-500 text-sm">Loading dashboard…</div>;
    return (
        <>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Commercial overview · {tenant?.season}</div>
                    <h1 className="font-sport text-4xl md:text-5xl leading-none uppercase" data-testid="dashboard-title">
                        {greeting()}, {user?.name?.split(" ")[0] || "there"}.
                    </h1>
                    <p className="font-editorial italic text-zinc-400 mt-3 text-lg">
                        Floodlights on. {kpis.active_sponsors} active sponsor{kpis.active_sponsors === 1 ? "" : "s"}. {kpis.renewals_soon_count} renewal{kpis.renewals_soon_count === 1 ? "" : "s"} this window.
                    </p>
                </div>
                <div className="inline-flex items-center gap-2 text-xs text-zinc-500"><CalendarClock size={14} /> Updated {formatDate(new Date().toISOString())}</div>
            </div>

            <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="kpi-strip">
                <KpiCard label="MRR" value={formatGBP(kpis.mrr_gbp)} testId="kpi-mrr" accent />
                <KpiCard label="Active sponsors" value={`${kpis.active_sponsors}/${kpis.total_sponsors}`} testId="kpi-sponsors" />
                <KpiCard label="Impressions, 30d" value={formatNumber(kpis.impressions_30d)} testId="kpi-impressions" />
                <KpiCard label="Renewals < 60d" value={kpis.renewals_soon_count} testId="kpi-renewals" />
                <KpiCard label="Unpaid invoices" value={kpis.unpaid_invoices_count} sub={`${formatGBP(kpis.unpaid_invoices_amount_gbp)} outstanding`} testId="kpi-unpaid" />
                <KpiCard label="Capacity sold" value={`${kpis.capacity_pct}%`} sub={`${formatGBP(kpis.capacity_sold_gbp)} of ${formatGBP(kpis.capacity_total_gbp)}`} testId="kpi-capacity" />
            </section>

            <CommercialTransparency kpis={kpis} />

            <section className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                <div className="lg:col-span-5 border hairline p-8 flex items-center gap-8" data-testid="capacity-card">
                    <CapacityDonut value={kpis.capacity_pct} />
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Inventory this season</div>
                        <div className="font-display font-extrabold text-3xl tracking-tightest">{formatGBP(kpis.capacity_sold_gbp)}</div>
                        <div className="text-sm text-zinc-400">of {formatGBP(kpis.capacity_total_gbp)} target</div>
                    </div>
                </div>
                <div className="lg:col-span-7 border hairline p-8" data-testid="tier-mix">
                    <div className="flex items-center justify-between mb-6">
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Sponsors by tier</div>
                        <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-600">{kpis.active_sponsors} active</span>
                    </div>
                    <div className="space-y-3">
                        {kpis.tier_mix.length === 0 ? (
                            <div className="text-xs text-zinc-500">No active sponsors yet. Add your first one.</div>
                        ) : kpis.tier_mix.map((row) => {
                            const total = kpis.tier_mix.reduce((a, b) => a + b.count, 0);
                            const pct = total ? (row.count / total) * 100 : 0;
                            return (
                                <div key={row.tier} className="grid grid-cols-12 items-center gap-4">
                                    <div className="col-span-3 text-sm text-zinc-300">{row.tier}</div>
                                    <div className="col-span-7 h-1.5 bg-white/[0.06] relative overflow-hidden">
                                        <div className="h-full" style={{ width: `${pct}%`, background: "var(--tenant-primary)" }} />
                                    </div>
                                    <div className="col-span-2 text-right font-display font-bold tracking-tight">{row.count}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                <div className="lg:col-span-6 border hairline p-7" data-testid="renewals-card">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Renewals</div>
                            <h3 className="font-display font-extrabold text-xl tracking-tight">Coming up soon</h3>
                        </div>
                        <CalendarClock size={16} className="text-zinc-500" />
                    </div>
                    {kpis.renewals_soon.length === 0 ? <div className="text-xs text-zinc-500">No renewals in the next 60 days.</div> : (
                        <div className="space-y-3">
                            {kpis.renewals_soon.map((r) => (
                                <div key={r.sponsor_id} className="flex items-center justify-between border-t hairline pt-3" data-testid={`renewal-${r.sponsor_id}`}>
                                    <div className="min-w-0">
                                        <div className="text-sm text-white truncate">{r.sponsor_name}</div>
                                        <div className="text-[11px] text-zinc-500">{formatDate(r.date)} · {r.days_out} days out</div>
                                    </div>
                                    <div className="font-display font-bold tracking-tight">{formatGBP(r.monthly_gbp)}<span className="text-zinc-500 text-xs font-normal">/mo</span></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="lg:col-span-6 border hairline p-7" data-testid="invoices-card">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Invoices</div>
                            <h3 className="font-display font-extrabold text-xl tracking-tight">Recent six</h3>
                        </div>
                        <Receipt size={16} className="text-zinc-500" />
                    </div>
                    {kpis.recent_invoices.length === 0 ? <div className="text-xs text-zinc-500">No invoices yet.</div> : (
                        <div className="space-y-2.5">
                            {kpis.recent_invoices.map((inv) => (
                                <div key={inv.id} className="flex items-center justify-between border-t hairline pt-3" data-testid={`invoice-${inv.id}`}>
                                    <div className="min-w-0">
                                        <div className="text-xs text-zinc-500 font-mono">{inv.id}</div>
                                        <div className="text-sm text-white truncate">{inv.sponsor_name}</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Pill tone={inv.status === "Paid" ? "paid" : "unpaid"}>{inv.status}</Pill>
                                        <div className="font-display font-bold tracking-tight w-16 text-right">{formatGBP(inv.amount_gbp)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
};

// Pricing transparency — what Floodlight takes vs what the club nets.
const CommercialTransparency = ({ kpis }) => {
    if (!kpis) return null;
    const isCommission = kpis.pricing_mode === "commission";
    const pct = kpis.commission_pct ?? 15;
    const hasStripe = kpis.stripe && !kpis.stripe.error;
    return (
        <section className="border hairline p-7 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center" data-testid="commercial-transparency"
            style={{ background: "linear-gradient(180deg, rgba(245,158,11,0.04) 0%, rgba(245,158,11,0) 100%)" }}>
            <div className="lg:col-span-4">
                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Commercial transparency</div>
                <h3 className="font-display font-extrabold text-2xl tracking-tight">Where every pound goes</h3>
                <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
                    {isCommission ? (
                        <>You're on Floodlight's <span className="text-white font-semibold">{pct}% commission</span> plan. We only get paid when you do — Stripe transaction fees on your behalf are absorbed by Floodlight{!kpis.transaction_fees_absorbed ? " (this club is configured to pay them itself)" : ""}.</>
                    ) : (
                        <>You're on a flat-fee plan. Floodlight doesn't take a commission on your sponsorship revenue.</>
                    )}
                </p>
                {hasStripe && (() => {
                    const sponGross = kpis.sponsorship_mrr_gbp || 0;
                    const fanGross = kpis.subscription_mrr_gbp || 0;
                    const ratio = isCommission ? (1 - pct / 100) : 1;
                    const sponNet = sponGross * ratio;
                    const fanNet = fanGross * ratio;
                    return (
                        <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
                            Combined view: sponsorship MRR <span className="text-zinc-300">{formatGBP(sponNet)}</span>
                            {" + "}
                            fan-app subscriptions <span className="text-zinc-300">{formatGBP(fanNet)}</span>
                            {" = "}
                            <span className="text-white font-semibold">{formatGBP(kpis.club_net_gbp)}</span> net monthly
                            {isCommission && (
                                <span className="text-zinc-600"> (after {pct}% fee on {formatGBP(sponGross + fanGross)} gross)</span>
                            )}.
                        </p>
                    );
                })()}
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border hairline p-5" data-testid="transparency-mrr">
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{hasStripe ? "Combined MRR · net" : "Sponsorship MRR · net"}</div>
                    <div className="font-display font-extrabold text-3xl tracking-tightest mt-2">{formatGBP(kpis.club_net_gbp)}</div>
                    <div className="text-[11px] text-zinc-500 mt-2">
                        {hasStripe
                            ? `${formatGBP((kpis.sponsorship_mrr_gbp || 0) + (kpis.subscription_mrr_gbp || 0))} gross · −${pct}% fee`
                            : `${formatGBP(kpis.mrr_gbp)} gross · −${pct}% fee`}
                    </div>
                </div>
                <div className="border p-5" style={{ borderColor: "rgba(245,158,11,0.4)" }} data-testid="transparency-soubor">
                    <div className="text-[10px] uppercase tracking-ultra-wide text-[var(--sb-amber)]">Floodlight's share</div>
                    <div className="font-display font-extrabold text-3xl tracking-tightest mt-2 text-[var(--sb-amber)]">- {formatGBP(kpis.soubor_take_gbp)}</div>
                    <div className="text-[11px] text-zinc-500 mt-2">{isCommission ? `${pct}% · transaction fees ${kpis.transaction_fees_absorbed ? "absorbed by Floodlight" : "paid by club"}` : "flat monthly fee"}</div>
                </div>
                <div className="border p-5" style={{ borderColor: "var(--tenant-primary)" }} data-testid="transparency-net">
                    <div className="text-[10px] uppercase tracking-ultra-wide" style={{ color: "var(--tenant-primary)" }}>You net</div>
                    <div className="font-display font-extrabold text-3xl tracking-tightest mt-2" style={{ color: "var(--tenant-primary)" }}>{formatGBP(kpis.club_net_gbp)}</div>
                    <div className="text-[11px] text-zinc-500 mt-2">straight to your bank</div>
                </div>
            </div>
        </section>
    );
};

// --- SPONSORS ---

// Modal: send a sponsor their portal login. Creates (or resets) the User, returns
// the one-time temp password, and shows a copy-friendly card the club admin can
// paste into an email or text message.
const SponsorInviteModal = ({ sponsor, tenant, onClose }) => {
    const [email, setEmail] = useState(sponsor?.contact_email || "");
    const [name, setName] = useState(sponsor?.contact_name || sponsor?.name || "");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null); // { email, temp_password, login_path, action }
    const [showPw, setShowPw] = useState(false);
    const [copied, setCopied] = useState("");

    React.useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    }, [onClose]);

    const submit = async (e) => {
        e?.preventDefault?.();
        setErr(""); setBusy(true);
        try {
            const { data } = await api.post(`/sponsors/${sponsor.id}/invite`, { email, name });
            setResult(data);
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally { setBusy(false); }
    };

    const copy = async (text, key) => {
        try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1800); } catch { /* ignore */ }
    };

    const loginUrl = result ? `${window.location.origin}${result.login_path}` : "";
    const inviteMessage = result ? `Hi ${result.name},

Your ${result.tenant_name || tenant?.name} sponsor portal is ready.

Sign in here: ${loginUrl}
Email: ${result.email}
Temporary password: ${result.temp_password}

You can change your password once you're in.` : "";

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center px-4 py-10 overflow-y-auto"
            data-testid="sponsor-invite-modal"
        >
            <div className="w-full max-w-lg bg-[#0a0a0a] border hairline relative p-8 md:p-10">
                <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white p-2" aria-label="Close" data-testid="invite-modal-close">
                    <X size={18} />
                </button>

                {!result ? (
                    <form onSubmit={submit}>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">Sponsor portal access</div>
                        <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-2">Invite {sponsor.name}</h3>
                        <p className="text-sm text-zinc-400 mb-7">We'll create a portal login and give you a one-time password to share. They can change it after first sign-in.</p>
                        <div className="space-y-4">
                            <div>
                                <label className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Contact name</label>
                                <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Maria Briggs"
                                    className="mt-2 w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none transition-colors"
                                    data-testid="invite-name" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Contact email</label>
                                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="maria@halton-heating.co.uk"
                                    className="mt-2 w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none transition-colors"
                                    data-testid="invite-email" />
                            </div>
                        </div>
                        {err && <div className="mt-5 border border-rose-500/40 text-rose-300 text-xs px-3 py-2.5">{err}</div>}
                        <button type="submit" disabled={busy || !email || !name}
                            className="w-full mt-7 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold transition-all hover:brightness-110 disabled:opacity-50"
                            style={{ background: "var(--tenant-primary)", color: "var(--tenant-primary-foreground, #000)" }}
                            data-testid="invite-submit">
                            {busy ? "Creating…" : <>Create login <Send size={14} /></>}
                        </button>
                    </form>
                ) : (
                    <div data-testid="invite-result">
                        <div className="text-[10px] uppercase tracking-ultra-wide mb-2" style={{ color: "var(--tenant-primary)" }}>
                            Login {result.action === "reset" ? "reset" : "created"}
                        </div>
                        <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-2">Send this to {result.name}.</h3>
                        <p className="text-sm text-zinc-400 mb-6">One-time temporary password. Copy it now, you won't see it again.</p>

                        <div className="space-y-3 mb-5">
                            <CopyRow label="Sign-in page" value={loginUrl} onCopy={() => copy(loginUrl, "url")} copied={copied === "url"} testId="invite-url" />
                            <CopyRow label="Email" value={result.email} onCopy={() => copy(result.email, "email")} copied={copied === "email"} testId="invite-out-email" />
                            <div data-testid="invite-out-password-row">
                                <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1.5">Temporary password</div>
                                <div className="flex items-stretch gap-2">
                                    <code className="flex-1 font-mono text-sm text-white border hairline px-3 py-2 select-all break-all flex items-center" data-testid="invite-out-password">
                                        {showPw ? result.temp_password : "•".repeat(result.temp_password.length)}
                                    </code>
                                    <button onClick={() => setShowPw((s) => !s)} className="border hairline px-3 hover:bg-white/5" aria-label="Show password" data-testid="invite-show-password">
                                        {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                    <button onClick={() => copy(result.temp_password, "pw")} className="border hairline px-3 hover:bg-white/5 inline-flex items-center gap-1.5 text-xs" data-testid="invite-copy-password">
                                        {copied === "pw" ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => copy(inviteMessage, "all")}
                            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold transition-all hover:brightness-110"
                            style={{ background: "var(--tenant-primary)", color: "var(--tenant-primary-foreground, #000)" }}
                            data-testid="invite-copy-all"
                        >
                            {copied === "all" ? <><Check size={14} /> Copied invite message</> : <><Copy size={14} /> Copy ready-to-paste invite</>}
                        </button>

                        <a
                            href={`mailto:${result.email}?subject=${encodeURIComponent(`Your ${result.tenant_name || tenant?.name || 'sponsor'} portal login`)}&body=${encodeURIComponent(inviteMessage)}`}
                            className="mt-3 w-full inline-flex items-center justify-center gap-2 border hairline px-5 py-3 text-sm hover:bg-white/5 transition-colors"
                            data-testid="invite-email-link"
                        >
                            Or open your email client
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

const CopyRow = ({ label, value, onCopy, copied, testId }) => (
    <div data-testid={testId}>
        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1.5">{label}</div>
        <div className="flex items-stretch gap-2">
            <code className="flex-1 font-mono text-sm text-white border hairline px-3 py-2 select-all break-all flex items-center">{value}</code>
            <button onClick={onCopy} className="border hairline px-3 hover:bg-white/5 inline-flex items-center gap-1.5 text-xs">
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
        </div>
    </div>
);

// Tenant-branded sponsor login share card. Shows the URL Ian sends to his sponsors,
// alongside a tenant-coloured QR code he can drop into matchday programmes / screens.
const SponsorLoginShareCard = ({ tenant }) => {
    const [copied, setCopied] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);
    if (!tenant?.slug) return null;

    // Subdomain wildcards aren't supported on the deployment platform, so we always
    // route through the apex /c/{slug} path on production. Local/preview hosts
    // still get the correct origin via window.location.origin.
    const origin = typeof window !== "undefined" ? window.location.origin : "https://floodlight.club";
    // Subdomain wildcards aren't supported by the deployment platform, so we use a
    // clean root-level slug as the primary URL (floodlight.club/rlfc). The longer
    // /c/{slug} form still resolves for backwards compatibility.
    const url = `${origin}/${tenant.slug}`;

    const onCopy = async (text, setter) => {
        try {
            await navigator.clipboard.writeText(text);
            setter(true);
            setTimeout(() => setter(false), 1800);
        } catch {
            // ignore
        }
    };

    return (
        <>
            <div
                className="border hairline p-6 md:p-7 relative overflow-hidden"
                data-testid="sponsor-login-share-card"
                style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 60%)" }}
            >
                <div
                    aria-hidden
                    className="absolute -right-20 -bottom-20 w-72 h-72 rounded-full opacity-[0.07] blur-3xl"
                    style={{ background: "var(--tenant-primary)" }}
                />
                <div className="relative flex flex-col md:flex-row md:items-center gap-5">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                        <Crest tenant={tenant} size={48} />
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-ultra-wide mb-1" style={{ color: "var(--tenant-primary)" }}>Your sponsor login link</div>
                            <div className="font-display font-bold text-base md:text-lg tracking-tight">Send this to your sponsors</div>
                            <p className="text-[11px] text-zinc-500 mt-0.5 max-w-md">
                                They land on a {tenant.short_name || tenant.name}-branded page. No Floodlight branding distracting them. Just your club.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 md:items-end">
                        <code className="font-mono text-xs md:text-sm text-zinc-200 border hairline px-3 py-2 select-all break-all" data-testid="sponsor-login-url">{url}</code>
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                onClick={() => onCopy(url, setCopied)}
                                className="inline-flex items-center gap-1.5 border hairline px-3 py-1.5 text-xs hover:bg-white/5 transition-colors"
                                data-testid="sponsor-login-copy"
                                style={copied ? { borderColor: "var(--tenant-primary)", color: "var(--tenant-primary)" } : {}}
                            >
                                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy link</>}
                            </button>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 border hairline px-3 py-1.5 text-xs hover:bg-white/5 transition-colors"
                                data-testid="sponsor-login-preview"
                            >
                                <ExternalLink size={12} /> Preview
                            </a>
                            <button
                                onClick={() => setQrOpen(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-all hover:brightness-110"
                                style={{ background: "var(--tenant-primary)", color: "var(--tenant-primary-foreground, #000)" }}
                                data-testid="sponsor-login-qr-open"
                            >
                                <QrCode size={12} /> QR code
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {qrOpen && <SponsorLoginQrModal tenant={tenant} url={url} onClose={() => setQrOpen(false)} />}
        </>
    );
};

// Branded QR-code modal — preview + download PNG.
const SponsorLoginQrModal = ({ tenant, url, onClose }) => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const qrSrc = `${API}/api/public/tenants/${tenant.slug}/qr.png?url=${encodeURIComponent(url)}`;

    React.useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center px-4 py-10 overflow-y-auto"
            data-testid="sponsor-login-qr-modal"
        >
            <div className="w-full max-w-md bg-[#0a0a0a] border hairline relative p-8 md:p-10">
                <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white p-2" aria-label="Close" data-testid="qr-modal-close">
                    <X size={18} />
                </button>
                <div className="text-center">
                    <div className="text-[10px] uppercase tracking-ultra-wide mb-2" style={{ color: "var(--tenant-primary)" }}>
                        {tenant.short_name || tenant.name} sponsor portal
                    </div>
                    <div className="font-display font-bold text-xl md:text-2xl tracking-tight mb-1">Scan to sign in.</div>
                    <p className="text-xs text-zinc-500 mb-6">Print it on programmes. Show it on the screen at half time. Stick it on the back of a business card.</p>
                    <div className="p-4 border hairline inline-block bg-[#050505]" data-testid="qr-modal-image-wrap">
                        <img src={qrSrc} alt={`QR code for ${tenant.name} sponsor portal`} width={260} height={260} data-testid="qr-modal-image" />
                    </div>
                    <div className="mt-5 text-[11px] font-mono text-zinc-500 break-all">{url}</div>
                    <a
                        href={qrSrc}
                        download={`floodlight-${tenant.slug}-qr.png`}
                        className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold transition-all hover:brightness-110 w-full"
                        style={{ background: "var(--tenant-primary)", color: "var(--tenant-primary-foreground, #000)" }}
                        data-testid="qr-modal-download"
                    >
                        <Download size={14} /> Download PNG
                    </a>
                </div>
            </div>
        </div>
    );
};

const SponsorsTab = ({ sponsors, packages, onEdit, onDelete, onTogglePause, onInvite, tenant }) => {
    const [filter, setFilter] = useState("");
    const filtered = sponsors.filter((s) => !filter || s.name.toLowerCase().includes(filter.toLowerCase()) || (s.contact_name || "").toLowerCase().includes(filter.toLowerCase()));
    const pkgById = Object.fromEntries(packages.map((p) => [p.id, p]));
    return (
        <>
            <SponsorLoginShareCard tenant={tenant} />

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Sponsors</div>
                    <h1 className="font-sport text-4xl md:text-5xl leading-none uppercase">All sponsors</h1>
                    <p className="font-editorial italic text-zinc-400 mt-3 text-lg">{sponsors.length} on the books. Tap any row to edit.</p>
                </div>
                <div className="flex items-center gap-2 border hairline px-3 py-2 w-72">
                    <Search size={14} className="text-zinc-500" />
                    <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter sponsors…" className="bg-transparent text-sm text-white placeholder:text-zinc-600 outline-none flex-1" data-testid="sponsors-filter" />
                </div>
            </div>

            <div className="border hairline overflow-hidden" data-testid="sponsors-table">
                <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b hairline text-[10px] uppercase tracking-ultra-wide text-zinc-500">
                    <div className="col-span-3">Sponsor</div>
                    <div className="col-span-2">Tier · Package</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Monthly</div>
                    <div className="col-span-2">Renewal</div>
                    <div className="col-span-1 text-right">Actions</div>
                </div>
                {filtered.length === 0 ? (
                    <div className="px-6 py-16 text-center text-sm text-zinc-500" data-testid="sponsors-empty">No sponsors match.</div>
                ) : filtered.map((s, i) => {
                    const pkg = pkgById[s.package_id];
                    return (
                        <div key={s.id} data-testid={`sponsor-row-${s.id}`} className={`grid grid-cols-12 items-center px-6 py-4 text-sm hover:bg-white/[0.025] transition-colors ${i !== 0 ? "border-t hairline" : ""}`}>
                            <div className="col-span-12 md:col-span-3 flex items-center gap-3">
                                <div className="w-9 h-9 bg-white/[0.04] border hairline flex items-center justify-center text-[10px] font-bold tracking-tight text-zinc-300">
                                    {s.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-white truncate">{s.name}</div>
                                    <div className="text-[11px] text-zinc-500 truncate">{s.contact_name || s.contact_email}</div>
                                </div>
                            </div>
                            <div className="col-span-6 md:col-span-2 mt-2 md:mt-0 text-zinc-300">
                                <div>{s.tier}</div>
                                {pkg && <div className="text-[11px] text-zinc-500 truncate">{pkg.name}</div>}
                            </div>
                            <div className="col-span-6 md:col-span-2 mt-2 md:mt-0">
                                <Pill tone={STATUS_TONE(s.status)} testId={`sponsor-status-${s.id}`}>
                                    <span className="kpi-pip" style={{ background: STATUS_TONE(s.status) === "active" ? "#10B981" : STATUS_TONE(s.status) === "pending" ? "#F59E0B" : "#52525B" }} />
                                    {s.status}
                                </Pill>
                            </div>
                            <div className="col-span-6 md:col-span-2 mt-2 md:mt-0 font-display font-bold tracking-tight">
                                {formatGBP(s.monthly_gbp)}<span className="text-zinc-500 font-normal text-xs"> /mo</span>
                            </div>
                            <div className="col-span-6 md:col-span-2 mt-2 md:mt-0 text-zinc-300 text-sm">{formatDate(s.renewal_date)}</div>
                            <div className="col-span-12 md:col-span-1 mt-2 md:mt-0 flex items-center justify-end gap-1">
                                <button onClick={() => onInvite(s)} className="p-1.5 text-zinc-400 hover:text-white" data-testid={`sponsor-invite-${s.id}`} aria-label="Invite to portal" title="Create sponsor login">
                                    <Send size={13} />
                                </button>
                                <button onClick={() => onTogglePause(s)} className="p-1.5 text-zinc-400 hover:text-white" data-testid={`sponsor-pause-${s.id}`} aria-label="Toggle pause">
                                    {s.status === "Paused" ? <Play size={13} /> : <Pause size={13} />}
                                </button>
                                <button onClick={() => onEdit(s)} className="p-1.5 text-zinc-400 hover:text-white" data-testid={`sponsor-edit-${s.id}`} aria-label="Edit">
                                    <Edit3 size={13} />
                                </button>
                                <button onClick={() => onDelete(s)} className="p-1.5 text-rose-400 hover:text-rose-300" data-testid={`sponsor-delete-${s.id}`} aria-label="Delete">
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

// --- PACKAGES ---
const PackagesTab = ({ packages, onEdit, onDelete }) => (
    <>
        <div>
            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Packages</div>
            <h1 className="font-sport text-4xl md:text-5xl leading-none uppercase">Inventory you can sell</h1>
            <p className="font-editorial italic text-zinc-400 mt-3 text-lg">Sponsorship products with tiered pricing &amp; deliverables.</p>
        </div>
        {packages.length === 0 ? (
            <div className="border hairline p-12 text-center" data-testid="packages-empty">
                <PackageIcon size={28} strokeWidth={1.4} className="text-zinc-500 mx-auto mb-4" />
                <h3 className="font-display font-bold text-xl tracking-tight">No packages defined</h3>
                <p className="text-sm text-zinc-500 mt-2">Build out your sponsorship tiers — headline, stand, perimeter, matchball.</p>
            </div>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {packages.map((p) => {
                    const pct = p.max_slots ? Math.round((p.used_slots / p.max_slots) * 100) : 0;
                    return (
                        <div key={p.id} className="border hairline p-6 hover:border-white/20 transition-colors flex flex-col gap-5" data-testid={`package-${p.id}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{p.tier}</div>
                                    <div className="font-display font-extrabold text-xl tracking-tight mt-1">{p.name}</div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => onEdit(p)} className="p-1.5 text-zinc-400 hover:text-white" data-testid={`package-edit-${p.id}`} aria-label="Edit">
                                        <Edit3 size={13} />
                                    </button>
                                    <button onClick={() => onDelete(p)} className="p-1.5 text-rose-400 hover:text-rose-300" data-testid={`package-delete-${p.id}`} aria-label="Delete">
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="font-display font-extrabold text-3xl tracking-tightest">{formatGBP(p.monthly_gbp)}</span>
                                <span className="text-xs text-zinc-500">/mo · {formatGBP(p.setup_gbp)} setup</span>
                            </div>
                            <div>
                                <div className="flex items-center justify-between text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1.5">
                                    <span>Slots filled</span>
                                    <span>{p.used_slots} / {p.max_slots}</span>
                                </div>
                                <div className="h-1 bg-white/[0.06] overflow-hidden">
                                    <div className="h-full" style={{ width: `${Math.min(100, pct)}%`, background: "var(--tenant-primary)" }} />
                                </div>
                            </div>
                            {p.deliverables?.length > 0 && (
                                <div data-testid={`package-pitch-block-${p.id}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-[9px] uppercase tracking-ultra-wide px-1.5 py-0.5 border" style={{ borderColor: "var(--tenant-primary)", color: "var(--tenant-primary)" }}>On the pitch</span>
                                        <span className="text-[10px] text-zinc-500 uppercase tracking-ultra-wide">
                                            {p.deliverables.length} item{p.deliverables.length === 1 ? "" : "s"}
                                        </span>
                                    </div>
                                    <ul className="space-y-1.5 text-sm text-zinc-400">
                                        {p.deliverables.slice(0, 4).map((d, i) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <span className="kpi-pip mt-2" style={{ background: "var(--tenant-primary)" }} />
                                                <span>{d}</span>
                                            </li>
                                        ))}
                                        {p.deliverables.length > 4 && <li className="text-xs text-zinc-600">+ {p.deliverables.length - 4} more</li>}
                                    </ul>
                                </div>
                            )}
                            {p.digital_ads?.enabled && (
                                <div className="border-t hairline pt-3 -mx-6 px-6 -mb-6 pb-3" data-testid={`package-ads-badge-${p.id}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-[9px] uppercase tracking-ultra-wide px-1.5 py-0.5" style={{ background: "var(--sb-amber)", color: "#000" }}>In the app</span>
                                        <span className="text-[10px] text-zinc-500 uppercase tracking-ultra-wide">
                                            {(p.digital_ads.placements || []).length} placement{(p.digital_ads.placements || []).length === 1 ? "" : "s"} · {(p.digital_ads.platforms || []).length} platform{(p.digital_ads.platforms || []).length === 1 ? "" : "s"}
                                        </span>
                                    </div>
                                    <div className="text-[11px] text-zinc-500">
                                        {p.digital_ads.rule_type === "none" && "Always-on campaign"}
                                        {p.digital_ads.rule_type === "period" && `Period: ${(p.digital_ads.period_start || "—").slice(0, 10)} → ${(p.digital_ads.period_end || "—").slice(0, 10)}`}
                                        {p.digital_ads.rule_type === "impressions" && `Capped at ${p.digital_ads.impressions_cap?.toLocaleString() || 0} impressions`}
                                        {p.digital_ads.rule_type === "clicks" && `Capped at ${p.digital_ads.clicks_cap?.toLocaleString() || 0} clicks`}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        )}
    </>
);

/* --- SETTINGS --- */
const SettingsTab = ({ tenant, onTenantUpdate }) => {
    const enabled = !!tenant?.matchday_page_enabled;
    const [intro, setIntro] = useState(tenant?.matchday_page_intro || "");
    const [savingIntro, setSavingIntro] = useState(false);
    const [introSaved, setIntroSaved] = useState(false);
    const [copied, setCopied] = useState(false);
    const [fbClub, setFbClub] = useState(tenant?.firebase_club_slug || "");
    const [fbTeam, setFbTeam] = useState(tenant?.firebase_team_slug || "mens-first-team");
    const [savingFb, setSavingFb] = useState(false);
    const [fbSaved, setFbSaved] = useState(false);

    const matchdayUrl = `${window.location.origin}/${tenant?.slug}/spots`;

    const toggle = async () => {
        await onTenantUpdate({ matchday_page_enabled: !enabled });
    };
    const saveIntro = async () => {
        setSavingIntro(true);
        try {
            await onTenantUpdate({ matchday_page_intro: intro });
            setIntroSaved(true);
            setTimeout(() => setIntroSaved(false), 1800);
        } finally { setSavingIntro(false); }
    };
    const copyUrl = async () => {
        try { await navigator.clipboard.writeText(matchdayUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* ignore */ }
    };
    const saveFb = async () => {
        setSavingFb(true);
        try {
            await onTenantUpdate({ firebase_club_slug: fbClub.trim() || null, firebase_team_slug: fbTeam.trim() || null });
            setFbSaved(true);
            setTimeout(() => setFbSaved(false), 1800);
        } finally { setSavingFb(false); }
    };
    const fbDirty = (fbClub || "") !== (tenant?.firebase_club_slug || "") || (fbTeam || "") !== (tenant?.firebase_team_slug || "");

    return (
        <div className="space-y-8" data-testid="settings-tab">
            <header>
                <h2 className="font-display font-bold text-2xl tracking-tight">Club settings</h2>
                <p className="text-sm text-zinc-400 mt-1">Visibility and matchday tools.</p>
            </header>

            <section className="border hairline p-6 md:p-7" data-testid="settings-matchday-card">
                <div className="flex items-start justify-between gap-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide mb-1" style={{ color: "var(--tenant-primary)" }}>Matchday spots page</div>
                        <h3 className="font-display font-bold text-lg md:text-xl tracking-tight">Let prospects see what's available.</h3>
                        <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
                            A public, fully-branded showcase at <code className="font-mono text-zinc-200">{matchdayUrl}</code>. Show open sponsorship spots, available packages, and capture enquiries straight into the Enquiries tab. Perfect for matchday QR codes on the big screen.
                        </p>
                    </div>
                    <button
                        onClick={toggle}
                        className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-all hover:brightness-110"
                        style={{ background: enabled ? "var(--tenant-primary)" : "transparent", color: enabled ? "var(--tenant-primary-foreground, #000)" : "#fff", border: enabled ? "none" : "1px solid rgba(255,255,255,0.15)" }}
                        data-testid="settings-matchday-toggle"
                    >
                        {enabled ? <><ToggleRight size={14} /> On</> : <><ToggleLeft size={14} /> Off</>}
                    </button>
                </div>

                {enabled && (
                    <div className="mt-6 border-t hairline pt-6 space-y-5">
                        <div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">Public URL</div>
                            <div className="flex items-stretch gap-2">
                                <code className="flex-1 font-mono text-sm text-white border hairline px-3 py-2 select-all break-all flex items-center" data-testid="settings-matchday-url">{matchdayUrl}</code>
                                <button onClick={copyUrl} className="border hairline px-3 inline-flex items-center gap-1.5 text-xs hover:bg-white/5" data-testid="settings-matchday-copy">
                                    {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                                </button>
                                <a href={matchdayUrl} target="_blank" rel="noopener noreferrer" className="border hairline px-3 inline-flex items-center gap-1.5 text-xs hover:bg-white/5" data-testid="settings-matchday-preview">
                                    <ExternalLink size={12} /> Preview
                                </a>
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">Hero intro (optional)</div>
                            <div className="flex items-stretch gap-2">
                                <textarea
                                    rows={2}
                                    value={intro}
                                    onChange={(e) => setIntro(e.target.value)}
                                    placeholder="Big home derby this Saturday. Pitchside boards, programme spreads, all open."
                                    className="flex-1 bg-transparent border hairline px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none resize-none"
                                    data-testid="settings-matchday-intro"
                                />
                                <button onClick={saveIntro} disabled={savingIntro || intro === (tenant?.matchday_page_intro || "")}
                                    className="border hairline px-3 inline-flex items-center gap-1.5 text-xs hover:bg-white/5 disabled:opacity-50"
                                    data-testid="settings-matchday-save-intro">
                                    {introSaved ? <><Check size={12} /> Saved</> : "Save"}
                                </button>
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-1.5">Shown as a quote on the showcase page.</div>
                        </div>
                    </div>
                )}
            </section>

            <section className="border hairline p-6 md:p-7" data-testid="settings-landing-card">
                <div className="flex items-start justify-between gap-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-[var(--sb-amber)] mb-1">Floodlight landing page</div>
                        <h3 className="font-display font-bold text-lg md:text-xl tracking-tight">Public visibility on floodlight.club</h3>
                        <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
                            When on, your club name appears in the "Trusted by clubs" strip on the public Floodlight homepage. Turn off if you'd rather stay private — your sponsor portal and matchday page aren't affected either way.
                        </p>
                    </div>
                    <button
                        onClick={() => onTenantUpdate({ show_on_landing: !(tenant?.show_on_landing !== false) })}
                        className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-all hover:brightness-110"
                        style={{
                            background: tenant?.show_on_landing !== false ? "var(--sb-amber)" : "transparent",
                            color: tenant?.show_on_landing !== false ? "#000" : "#fff",
                            border: tenant?.show_on_landing !== false ? "none" : "1px solid rgba(255,255,255,0.15)"
                        }}
                        data-testid="settings-show-on-landing-toggle"
                    >
                        {tenant?.show_on_landing !== false ? <><ToggleRight size={14} /> Public</> : <><ToggleLeft size={14} /> Hidden</>}
                    </button>
                </div>
            </section>

            <section className="border hairline p-6 md:p-7" data-testid="settings-firebase-card">
                <div className="mb-5">
                    <div className="text-[10px] uppercase tracking-ultra-wide mb-1" style={{ color: "var(--tenant-primary)" }}>Fan app sync · Firebase</div>
                    <h3 className="font-display font-bold text-lg md:text-xl tracking-tight">Where should the squad post to?</h3>
                    <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
                        Players you edit in the <span className="text-white">Squad</span> tab mirror to Firestore at
                        {" "}<code className="font-mono text-zinc-200 text-xs">clubs/{fbClub || "{club}"}/teams/{fbTeam || "mens-first-team"}/players</code>.
                        The <span className="text-white">Club slug</span> must match the top-level document key your fan app reads from.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Firebase club slug</label>
                        <input
                            value={fbClub}
                            onChange={(e) => setFbClub(e.target.value)}
                            placeholder="rlfc"
                            className="w-full mt-1.5 bg-transparent border hairline px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none font-mono"
                            data-testid="settings-fb-club-slug"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Team slug</label>
                        <input
                            value={fbTeam}
                            onChange={(e) => setFbTeam(e.target.value)}
                            placeholder="mens-first-team"
                            className="w-full mt-1.5 bg-transparent border hairline px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none font-mono"
                            data-testid="settings-fb-team-slug"
                        />
                    </div>
                </div>
                <div className="mt-5 flex items-center justify-between gap-4">
                    <div className="text-[11px] text-zinc-500">
                        Leave blank to disable Firebase sync entirely for this tenant.
                    </div>
                    <button
                        onClick={saveFb}
                        disabled={savingFb || !fbDirty}
                        className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold border hairline hover:bg-white/5 disabled:opacity-50"
                        data-testid="settings-fb-save"
                    >
                        {fbSaved ? <><Check size={12} /> Saved</> : savingFb ? "Saving…" : "Save Firebase mapping"}
                    </button>
                </div>
            </section>
        </div>
    );
};

/* --- ENQUIRIES --- */
const EnquiriesTab = ({ tenant }) => {
    const [enquiries, setEnquiries] = useState(null);
    const [filter, setFilter] = useState("new");

    useEffect(() => {
        api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => setEnquiries([]));
    }, []);

    if (!enquiries) {
        return <div className="text-sm text-zinc-500 py-12 text-center">Loading enquiries…</div>;
    }
    const filtered = filter === "all" ? enquiries : enquiries.filter((e) => e.status === filter);
    const counts = {
        new: enquiries.filter((e) => e.status === "new").length,
        contacted: enquiries.filter((e) => e.status === "contacted").length,
        closed: enquiries.filter((e) => e.status === "closed").length,
    };

    const updateStatus = async (id, status) => {
        await api.patch(`/enquiries/${id}`, { status });
        const r = await api.get("/enquiries");
        setEnquiries(r.data);
    };

    return (
        <div className="space-y-6" data-testid="enquiries-tab">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <h2 className="font-display font-bold text-2xl tracking-tight">Sponsor enquiries</h2>
                    <p className="text-sm text-zinc-400 mt-1">From your matchday showcase page. Reply within a day to keep momentum.</p>
                </div>
                {tenant?.matchday_page_enabled ? (
                    <span className="text-[10px] uppercase tracking-ultra-wide" style={{ color: "var(--tenant-primary)" }}>
                        <span className="kpi-pip mr-1.5 animate-pulse" style={{ background: "var(--tenant-primary)" }} /> Showcase live at /{tenant.slug}/spots
                    </span>
                ) : (
                    <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">
                        Showcase page is off. Enable in Settings.
                    </span>
                )}
            </header>
            <div className="flex items-center gap-2 text-xs">
                {[
                    { key: "new", label: `New (${counts.new})` },
                    { key: "contacted", label: `Contacted (${counts.contacted})` },
                    { key: "closed", label: `Closed (${counts.closed})` },
                    { key: "all", label: `All (${enquiries.length})` },
                ].map((f) => (
                    <button key={f.key} onClick={() => setFilter(f.key)}
                        className={`px-3 py-1.5 border hairline ${filter === f.key ? "bg-white/5 text-white border-white/30" : "text-zinc-400 hover:text-white"}`}
                        data-testid={`enquiries-filter-${f.key}`}>
                        {f.label}
                    </button>
                ))}
            </div>
            {filtered.length === 0 ? (
                <div className="border hairline p-12 text-center text-sm text-zinc-500">
                    No enquiries here yet. {tenant?.matchday_page_enabled ? "When prospects fill in the form on your showcase page, they'll land here." : "Enable the showcase page in Settings to start collecting enquiries."}
                </div>
            ) : (
                <div className="space-y-3">
                    {filtered.map((e) => (
                        <div key={e._id || e.id || e.created_ts} className="border hairline p-5 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid={`enquiry-${e.created_ts}`}>
                            <div className="md:col-span-4">
                                <div className="font-display font-semibold text-base tracking-tight">{e.company || e.name}</div>
                                <div className="text-xs text-zinc-500">{e.name}</div>
                                <div className="mt-2 flex flex-col gap-1 text-xs">
                                    <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-white"><Mail size={11} /> {e.email}</a>
                                    {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-white"><Phone size={11} /> {e.phone}</a>}
                                </div>
                            </div>
                            <div className="md:col-span-5">
                                {e.interest && <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Interested in</div>}
                                {e.interest && <div className="text-sm mt-0.5">{e.interest}</div>}
                                {e.notes && <p className="text-xs text-zinc-400 mt-2 leading-relaxed whitespace-pre-wrap">{e.notes}</p>}
                            </div>
                            <div className="md:col-span-3 flex flex-col gap-2 md:items-end">
                                <span className="inline-flex items-center gap-1.5 border rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-ultra-wide" style={{
                                    borderColor: e.status === "new" ? "rgba(245,158,11,0.5)" : e.status === "contacted" ? "rgba(99,102,241,0.5)" : "rgba(82,82,91,0.5)",
                                    color: e.status === "new" ? "#fbbf24" : e.status === "contacted" ? "#a5b4fc" : "#71717a",
                                }}>{e.status}</span>
                                <div className="text-[10px] text-zinc-600">{new Date(e.created_at).toLocaleString()}</div>
                                <div className="flex items-center gap-1.5">
                                    {e.status !== "contacted" && <button onClick={() => updateStatus(e.id || e._id, "contacted")} className="text-[10px] uppercase tracking-ultra-wide border hairline px-2 py-1 hover:bg-white/5" data-testid={`enquiry-contacted-${e.created_ts}`}>Mark contacted</button>}
                                    {e.status !== "closed" && <button onClick={() => updateStatus(e.id || e._id, "closed")} className="text-[10px] uppercase tracking-ultra-wide border hairline px-2 py-1 hover:bg-white/5" data-testid={`enquiry-close-${e.created_ts}`}>Close</button>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ComingSoon = ({ tab }) => (
    <div className="border hairline p-16 text-center" data-testid={`coming-soon-${tab}`}>
        <Sparkles size={28} strokeWidth={1.4} className="text-[var(--sb-amber)] mx-auto mb-4" />
        <h3 className="font-display font-bold text-2xl tracking-tight capitalize">{tab} — coming soon</h3>
        <p className="text-sm text-zinc-500 mt-3 max-w-md mx-auto">This area is on the Phase 2 roadmap. Sponsors and packages are live now — head back to overview to keep building.</p>
    </div>
);

// --- FORMS ---
const SponsorFormModal = ({ initial, packages, onClose, onSubmit }) => {
    // Backend returns `null` for unset optional fields. <input value={null}> renders
    // blank, so we coalesce nulls to empty strings (or numeric 0) before seeding the form.
    const seedFromInitial = (s) => ({
        name: s?.name ?? "",
        contact_name: s?.contact_name ?? "",
        contact_email: s?.contact_email ?? "",
        phone: s?.phone ?? "",
        website: s?.website ?? "",
        tier: s?.tier ?? "Perimeter",
        status: s?.status ?? "Active",
        monthly_gbp: s?.monthly_gbp ?? 0,
        setup_gbp: s?.setup_gbp ?? 0,
        package_id: s?.package_id ?? "",
        start_date: s?.start_date ?? "",
        renewal_date: s?.renewal_date ?? "",
        notes: s?.notes ?? "",
        digital_ads: s?.digital_ads ?? null,
        firestore_sponsor_id: s?.firestore_sponsor_id ?? "",
    });
    const [draft, setDraft] = useState(() => seedFromInitial(initial));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const onPackageChange = (id) => {
        const pkg = packages.find((p) => p.id === id);
        if (pkg) {
            setDraft((d) => ({ ...d, package_id: id, tier: pkg.tier, monthly_gbp: pkg.monthly_gbp, setup_gbp: pkg.setup_gbp }));
        } else {
            setDraft((d) => ({ ...d, package_id: "" }));
        }
    };

    const submit = async () => {
        try {
            setErr(""); setBusy(true);
            const payload = {
                ...draft,
                monthly_gbp: parseInt(draft.monthly_gbp || 0, 10),
                setup_gbp: parseInt(draft.setup_gbp || 0, 10),
                package_id: draft.package_id || null,
                contact_email: draft.contact_email || null,
                start_date: draft.start_date || null,
                renewal_date: draft.renewal_date || null,
                digital_ads: draft.digital_ads,
                firestore_sponsor_id: draft.firestore_sponsor_id || "",
            };
            await onSubmit(payload);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <Modal open onClose={onClose} wide testId="sponsor-form-modal" title={initial ? `Edit · ${initial.name}` : "Add a sponsor"}
            footer={<>
                <GhostButton onClick={onClose}>Cancel</GhostButton>
                <PrimaryButton tenantTheme onClick={submit} disabled={busy || !draft.name} data-testid="sponsor-form-submit">{busy ? "Saving…" : initial ? "Save changes" : "Create sponsor"}</PrimaryButton>
            </>}>
            <div className="grid grid-cols-12 gap-4">
                <Field label="Business name" span={8}>
                    <TextInput value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="Halton Heating Co." testId="sponsor-form-name" />
                </Field>
                <Field label="Tier" span={4} hint="Any name you like — e.g. Headline, Founders, Matchday, App Banner">
                    <TextInput value={draft.tier} onChange={(v) => setDraft({ ...draft, tier: v })} placeholder="Custom tier name" testId="sponsor-form-tier" />
                </Field>
                <Field label="Contact name" span={6}>
                    <TextInput value={draft.contact_name} onChange={(v) => setDraft({ ...draft, contact_name: v })} placeholder="Maria Briggs" testId="sponsor-form-contact" />
                </Field>
                <Field label="Contact email" span={6}>
                    <TextInput type="email" value={draft.contact_email} onChange={(v) => setDraft({ ...draft, contact_email: v })} placeholder="maria@halton-heating.co.uk" testId="sponsor-form-email" />
                </Field>
                <Field label="Phone" span={6}>
                    <TextInput value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} placeholder="01928 000111" testId="sponsor-form-phone" />
                </Field>
                <Field label="Website" span={6}>
                    <TextInput value={draft.website} onChange={(v) => setDraft({ ...draft, website: v })} placeholder="https://halton-heating.co.uk" testId="sponsor-form-website" />
                </Field>

                <Field label="Allocate to package" span={6}>
                    <Select value={draft.package_id || ""} onChange={onPackageChange} options={[{ value: "", label: "— No package —" }, ...packages.map((p) => ({ value: p.id, label: `${p.name} · £${p.monthly_gbp}/mo` }))]} testId="sponsor-form-package" />
                </Field>
                <Field label="Status" span={6}>
                    <Select value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })} options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))} testId="sponsor-form-status" />
                </Field>

                <Field label="Monthly fee (GBP)" span={4}>
                    <TextInput type="number" value={draft.monthly_gbp} onChange={(v) => setDraft({ ...draft, monthly_gbp: v })} testId="sponsor-form-monthly" />
                </Field>
                <Field label="Setup fee (GBP)" span={4}>
                    <TextInput type="number" value={draft.setup_gbp} onChange={(v) => setDraft({ ...draft, setup_gbp: v })} testId="sponsor-form-setup" />
                </Field>
                <Field label="Start date" span={4}>
                    <TextInput type="date" value={(draft.start_date || "").slice(0, 10)} onChange={(v) => setDraft({ ...draft, start_date: v })} testId="sponsor-form-start" />
                </Field>
                <Field label="Renewal date" span={6}>
                    <TextInput type="date" value={(draft.renewal_date || "").slice(0, 10)} onChange={(v) => setDraft({ ...draft, renewal_date: v })} testId="sponsor-form-renewal" />
                </Field>
                <Field label="Notes" span={12}>
                    <textarea value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                        rows={2} placeholder="Anything internal you want to remember about this sponsor."
                        className="w-full bg-transparent border hairline px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none transition-colors"
                        data-testid="sponsor-form-notes" />
                </Field>

                <Field label="Fan-app sponsor ID" span={12} hint="The Firestore doc ID under sponsors/ in your fan app — e.g. apec_taxis. Leave blank to disable live analytics.">
                    <TextInput value={draft.firestore_sponsor_id || ""} onChange={(v) => setDraft({ ...draft, firestore_sponsor_id: v })} placeholder="apec_taxis" testId="sponsor-form-firestore-id" />
                </Field>

                <div className="col-span-12">
                    <DigitalAdsForm
                        value={draft.digital_ads}
                        onChange={(v) => setDraft({ ...draft, digital_ads: v })}
                        testIdPrefix="sponsor-ads"
                        allowInherit
                        inheritedFromPackage={packages.find((p) => p.id === draft.package_id)?.digital_ads}
                    />
                </div>

                {err && <div className="col-span-12 text-xs border border-rose-500/40 text-rose-300 px-3 py-2.5">{err}</div>}
            </div>
        </Modal>
    );
};

const PackageFormModal = ({ initial, onClose, onSubmit }) => {
    const [draft, setDraft] = useState(() => initial || {
        name: "", tier: "Perimeter", setup_gbp: 0, monthly_gbp: 0, description: "", deliverables: [], max_slots: 1, active: true, digital_ads: null,
    });
    const [activeTab, setActiveTab] = useState("basics");
    const [delivInput, setDelivInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const addDeliv = () => {
        if (!delivInput.trim()) return;
        setDraft((d) => ({ ...d, deliverables: [...(d.deliverables || []), delivInput.trim()] }));
        setDelivInput("");
    };
    const rmDeliv = (i) => setDraft((d) => ({ ...d, deliverables: d.deliverables.filter((_, idx) => idx !== i) }));

    const submit = async () => {
        try {
            setErr(""); setBusy(true);
            await onSubmit({
                ...draft,
                setup_gbp: parseInt(draft.setup_gbp || 0, 10),
                monthly_gbp: parseInt(draft.monthly_gbp || 0, 10),
                max_slots: parseInt(draft.max_slots || 1, 10),
                digital_ads: draft.digital_ads,
            });
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <Modal open onClose={onClose} wide testId="package-form-modal" title={initial ? `Edit · ${initial.name}` : "New sponsorship package"}
            footer={<>
                <GhostButton onClick={onClose}>Cancel</GhostButton>
                <PrimaryButton tenantTheme onClick={submit} disabled={busy || !draft.name} data-testid="package-form-submit">{busy ? "Saving…" : initial ? "Save changes" : "Create package"}</PrimaryButton>
            </>}>
            {/* Tabs */}
            <div className="flex items-center gap-0 border-b hairline mb-6 -mt-2" data-testid="package-form-tabs">
                {[
                    { k: "basics",   label: "Basics" },
                    { k: "pitch",    label: "On the pitch" },
                    { k: "in_app",   label: "In the app" },
                ].map((t) => (
                    <button key={t.k} type="button" onClick={() => setActiveTab(t.k)}
                        className={`px-4 py-2.5 text-xs uppercase tracking-ultra-wide transition-colors border-b-2 -mb-px ${activeTab === t.k ? "border-[var(--sb-amber)] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
                        data-testid={`package-form-tab-${t.k}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {activeTab === "basics" && (
                <div className="grid grid-cols-12 gap-4" data-testid="package-form-tab-panel-basics">
                    <Field label="Name" span={8}>
                        <TextInput value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="Headline Partner" testId="package-form-name" />
                    </Field>
                    <Field label="Tier" span={4} hint="Any name you like — e.g. Headline, Founders, Digital">
                        <TextInput value={draft.tier} onChange={(v) => setDraft({ ...draft, tier: v })} placeholder="Custom tier name" testId="package-form-tier" />
                    </Field>
                    <Field label="Setup fee (GBP)" span={4}>
                        <TextInput type="number" value={draft.setup_gbp} onChange={(v) => setDraft({ ...draft, setup_gbp: v })} testId="package-form-setup" />
                    </Field>
                    <Field label="Monthly fee (GBP)" span={4}>
                        <TextInput type="number" value={draft.monthly_gbp} onChange={(v) => setDraft({ ...draft, monthly_gbp: v })} testId="package-form-monthly" />
                    </Field>
                    <Field label="Max slots" span={4}>
                        <TextInput type="number" value={draft.max_slots} onChange={(v) => setDraft({ ...draft, max_slots: v })} testId="package-form-slots" />
                    </Field>
                    <Field label="Description" span={12}>
                        <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                            rows={2} placeholder="What this package is, in plain English."
                            className="w-full bg-transparent border hairline px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none transition-colors"
                            data-testid="package-form-description" />
                    </Field>
                </div>
            )}

            {activeTab === "pitch" && (
                <div className="grid grid-cols-12 gap-4" data-testid="package-form-tab-panel-pitch">
                    <div className="col-span-12 border hairline px-4 py-3 flex items-start gap-3">
                        <span className="text-[9px] uppercase tracking-ultra-wide px-1.5 py-0.5 border mt-0.5" style={{ borderColor: "var(--tenant-primary)", color: "var(--tenant-primary)" }}>On the pitch</span>
                        <div className="text-[11px] text-zinc-400 leading-relaxed">
                            What the sponsor gets on matchday — perimeter boards, banners, stand naming, programme spots, hospitality. Anything physical or in-person at the ground.
                            <span className="block text-zinc-600 mt-1">App banners and interstitials live in the next tab.</span>
                        </div>
                    </div>
                    <Field label="Physical inventory items included" span={12} hint="Press Enter to add. These show up on the sponsor portal.">
                        <div className="flex items-center gap-2">
                            <TextInput value={delivInput} onChange={setDelivInput} placeholder="e.g. 8x2ft pitch-side board" testId="package-form-deliv-input"
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDeliv(); } }} />
                            <GhostButton onClick={addDeliv} data-testid="package-form-deliv-add">Add</GhostButton>
                        </div>
                        {draft.deliverables?.length > 0 && (
                            <ul className="mt-3 space-y-1.5">
                                {draft.deliverables.map((d, i) => (
                                    <li key={i} className="flex items-center justify-between border hairline px-3 py-2 text-sm" data-testid={`deliverable-${i}`}>
                                        <span className="inline-flex items-center gap-2"><BadgeCheck size={13} className="text-[var(--tenant-primary)]" /> {d}</span>
                                        <button onClick={() => rmDeliv(i)} className="text-rose-400 hover:text-rose-300 text-xs">Remove</button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Field>
                </div>
            )}

            {activeTab === "in_app" && (
                <div className="space-y-4" data-testid="package-form-tab-panel-in_app">
                    <div className="border hairline px-4 py-3 flex items-start gap-3">
                        <span className="text-[9px] uppercase tracking-ultra-wide px-1.5 py-0.5 mt-0.5" style={{ background: "var(--sb-amber)", color: "#000" }}>In the app</span>
                        <div className="text-[11px] text-zinc-400 leading-relaxed">
                            Digital ad placements inside your fan app — home banners, rectangles, internal-page banners, full-screen interstitials. Choose platforms and a campaign rule (always-on, period, or capped).
                        </div>
                    </div>
                    <DigitalAdsForm
                        value={draft.digital_ads}
                        onChange={(v) => setDraft({ ...draft, digital_ads: v })}
                        testIdPrefix="package-ads"
                    />
                </div>
            )}

            {err && <div className="mt-4 text-xs border border-rose-500/40 text-rose-300 px-3 py-2.5">{err}</div>}
        </Modal>
    );
};
